import React, { useState, useContext, useEffect } from 'react';
import Container from '../../atoms/Container';
import Header from '../../atoms/Header';
import { BaseImageType } from '../../atoms/Image/image';
import { useInView } from 'react-intersection-observer';
import { getCookie } from '../../../lib/cookie-utils';
import Plan from './plan';
import {
  Title,
  Description,
  Plans,
  StyledContainer,
  IconsArea,
  PaymentIcons,
  PaymentIconContainer,
  PaymentMethod,
  AdditionalPaymentIconsArea,
  AdditionalPaymentIconsSummary,
  AdditionalPaymentIconsContainer,
  ErrorBanner,
  ErrorMessage,
  ExitError,
  LegalDisclaimer,
  StorefrontBenefits,
  StorefrontBenefitsList,
  StorefrontListBenefit,
  StorefrontBenefitsHeading,
} from './components';
import Button from '../../atoms/Button';
import { IconX } from '@spotify-internal/encore-web';
import {
  eventParamAttributes,
  EventParamTypes,
} from '@spotify-internal/touchdown-analytics';
import { purify } from '../../utils/helpers';
import UserContext from '../../../configuration/context/user';
import DeviceContext from '../../../configuration/context/device';
import { trackEvent } from '../../../lib/tracking';
import { premiumStorefrontInViewEvent } from '../../../lib/tracking/eventPayloadDefinitions';
import MarketContext from '../../../configuration/context/market';

export interface HeaderWithBenefits {
  headingBenefitsLabel: string;
  headingBenefits: Array<string>;
  headerGradients: { gradientStart: string; gradientEnd: string };
}

export type StorefrontPropTypes = {
  header?: (React.ComponentProps<typeof Header> & HeaderWithBenefits) | null;
  icons?: BaseImageType[];
  labels?: {
    additionalPaymentMethods: string;
  };
  currentProductText?: string;
  errorMessage?: string;
  errorBanner?: boolean;
  plans: React.ComponentProps<typeof Plan>['plan'][];
  container?: React.ComponentProps<typeof Container> | null;
  hasDropShadow?: boolean;
  additionalCard?: {
    button?: React.ComponentProps<typeof Button>;
    content: string;
    eventParams?: EventParamTypes;
  };
  eventParams?: EventParamTypes;
  legalDisclaimer?: string;
};

export function TweakedStorefront({
  header = null,
  icons,
  labels,
  currentProductText,
  errorMessage,
  errorBanner = false,
  plans,
  container = null,
  hasDropShadow = true,
  additionalCard,
  eventParams,
  legalDisclaimer,
}: StorefrontPropTypes) {
  const user = useContext(UserContext);
  const market = useContext(MarketContext);

  const [isErrorVisible, setErrorVisibility] = useState<boolean>(
    !!user.ineligibility_flag || errorBanner,
  );
  const additionalPlans = additionalCard ? true : false;
  const [paymentIcons, setPaymentIcons] = useState<BaseImageType[] | null>(
    null,
  );
  const [additionalPaymentIcons, setAdditionalPaymentIcons] = useState<
    BaseImageType[] | null
  >(null);
  const [
    dynamicAdditionalPaymentMethodsLabel,
    setAdditionalPaymentMethodsLabel,
  ] = useState<string>('+');
  const device = useContext(DeviceContext);
  const [isGPBEligible, setIsGPBEligible] = useState<boolean>(
    device.platform === 'android' && device.gpb_supported,
  );

  function getCurrentPlan() {
    const currentPlan = plans?.find(obj => {
      return obj.isUsersCurrentProduct;
    });
    return currentPlan?.eventParams?.campaignId || false;
  }

  useEffect(() => {
    if (icons) {
      setPaymentIcons(icons);
    }

    if (paymentIcons && paymentIcons.length > 5) {
      setAdditionalPaymentIcons(paymentIcons.splice(4));
    }

    if (device.platform === 'android' && device.gpb_supported) {
      setIsGPBEligible(true);
    }

    if (
      additionalPaymentIcons &&
      additionalPaymentIcons.length &&
      labels?.additionalPaymentMethods
    ) {
      setAdditionalPaymentMethodsLabel(
        labels.additionalPaymentMethods.replace(
          '%REMAINING%',
          additionalPaymentIcons.length as unknown as string,
        ),
      );
    }
  }, [icons, paymentIcons, additionalPaymentIcons, labels, device]);

  const [ref, inView] = useInView({
    fallbackInView: true,
    threshold: 0.6,
    triggerOnce: true,
  });

  const getOrderedPlanOfferTypeIds = (
    planCards: React.ComponentProps<typeof Plan>['plan'][],
  ): Array<string> => {
    return planCards.map(plan => plan.offerTypeId ?? 'unknown');
  };

  useEffect(() => {
    if (inView === true) {
      const payload = {
        country: market?.country ?? 'unknown',
        locale: market?.language ?? 'unknown',
        full_url: window.location.href,
        timestamp: Math.floor(Date.now() / 1000),
        sorting_order: getOrderedPlanOfferTypeIds(plans),
        correlation_id: getCookie('sp_t'),
        is_evergreen: market?.isEvergreen,
        eligibility: user?.eligibility,
      };

      trackEvent(
        premiumStorefrontInViewEvent(payload, {
          authorize: user.authenticated,
        }),
      );
    }
  }, [inView, market, plans, user.authenticated, user.eligibility]);

  return (
    <StyledContainer
      {...container}
      {...eventParamAttributes(eventParams)}
      data-current-plan={getCurrentPlan()}
      data-storefront-error={errorMessage}
      data-payment-icons={!isGPBEligible}
    >
      {header && (
        <>
          {header.text && (
            <Title dangerouslySetInnerHTML={{ __html: purify(header.text) }} />
          )}
          {header.subheader && (
            <Description
              dangerouslySetInnerHTML={{ __html: purify(header.subheader) }}
            />
          )}
          {paymentIcons && !isGPBEligible && (
            <IconsArea>
              <PaymentIcons>
                {paymentIcons.map(({ sources, label }, i) => {
                  return (
                    <PaymentIconContainer key={`icon-${i}`}>
                      <PaymentMethod
                        sources={sources}
                        role="img"
                        aria-label={label}
                      />
                    </PaymentIconContainer>
                  );
                })}
              </PaymentIcons>
              {additionalPaymentIcons && (
                <AdditionalPaymentIconsArea>
                  <AdditionalPaymentIconsSummary tabIndex={0}>
                    <span>{dynamicAdditionalPaymentMethodsLabel}</span>
                    <AdditionalPaymentIconsContainer
                      amount={additionalPaymentIcons.length}
                    >
                      {additionalPaymentIcons.map(({ sources, label }, i) => {
                        return (
                          <PaymentIconContainer key={`icon-${i}`}>
                            <PaymentMethod
                              sources={sources}
                              role="img"
                              aria-label={label}
                            />
                          </PaymentIconContainer>
                        );
                      })}
                    </AdditionalPaymentIconsContainer>
                  </AdditionalPaymentIconsSummary>
                </AdditionalPaymentIconsArea>
              )}
            </IconsArea>
          )}
          {header.headingBenefitsLabel && header.headingBenefits && (
            <StorefrontBenefits {...header.headerGradients} ref={ref}>
              <div>
                <StorefrontBenefitsHeading
                  dangerouslySetInnerHTML={{
                    __html: purify(header.headingBenefitsLabel),
                  }}
                />
                <StorefrontBenefitsList>
                  {header.headingBenefits.map((benefit, index) => {
                    return (
                      <StorefrontListBenefit key={index}>
                        {benefit}
                      </StorefrontListBenefit>
                    );
                  })}
                </StorefrontBenefitsList>
              </div>
            </StorefrontBenefits>
          )}
          {isErrorVisible && errorMessage && (
            <div className="encore-dark-theme">
              <ErrorBanner>
                <ErrorMessage>{errorMessage}</ErrorMessage>
                <ExitError
                  role="button"
                  onClick={() => setErrorVisibility(false)}
                >
                  <IconX />
                </ExitError>
              </ErrorBanner>
            </div>
          )}
        </>
      )}
      {plans && (
        <Plans length={plans.length}>
          {plans.map(
            (plan, i) =>
              plan && (
                <Plan
                  key={`plan-${i}`}
                  plan={plan}
                  index={i}
                  currentProductText={currentProductText}
                  length={plans.length}
                  hasDropShadow={hasDropShadow}
                />
              ),
          )}
        </Plans>
      )}
      {legalDisclaimer && (
        <LegalDisclaimer
          defaultDisclaimer
          additionalPlans={additionalPlans}
          isGroupLegalDisclaimer
          text={legalDisclaimer}
        />
      )}
    </StyledContainer>
  );
}

export default TweakedStorefront;
